"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { productsData } from "@/data/products";
import { cn } from "@/lib/utils";
import Link from "next/link";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export function ProductScrollSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const track = scrollTrackRef.current;

    if (!container || !track) return;
    const slides = gsap.utils.toArray<HTMLElement>(".product-slide");

    const ctx = gsap.context(() => {
      gsap.to(track, {
        x: () => -(track.scrollWidth - window.innerWidth),
        ease: "none",
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: () => `+=${track.scrollWidth - window.innerWidth}`,
          pin: true,
          scrub: 1,
          snap: {
            snapTo: 1 / (slides.length - 1),
            duration: 0.35,
            ease: "power2.out"
          },
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            if (progressRef.current) {
              progressRef.current.style.width = `${self.progress * 100}%`;
            }
            setActiveIndex(
              Math.min(
                slides.length - 1,
                Math.round(self.progress * (slides.length - 1))
              )
            );
          },
        },
      });
    }, container);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="relative w-full bg-[#F7F5F0] overflow-hidden select-none">
      <div ref={scrollTrackRef} className="flex min-h-screen w-max items-center bg-[#F7F5F0]">
        {productsData.map((product, i) => (
          <section
            key={product.id}
            className="product-slide flex h-screen w-screen shrink-0 items-center justify-center px-6 sm:px-16 lg:px-24"
          >
            <div className="w-full max-w-[1360px] grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] items-center gap-14 lg:gap-24 xl:gap-32">

              {/* Left: Viewfinder-framed image */}
              <div className="relative aspect-[4/4] w-full">
                {/* corner brackets */}
                <span className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[#9C7A46]" />
                <span className="absolute -top-3 -right-3 w-8 h-8 border-t-2 border-r-2 border-[#9C7A46]" />
                <span className="absolute -bottom-3 -left-3 w-8 h-8 border-b-2 border-l-2 border-[#9C7A46]" />
                <span className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[#9C7A46]" />

                <div className="relative w-full h-full overflow-hidden bg-[#EAE4D8] group will-change-transform shadow-[0_1px_2px_rgba(23,22,15,0.05),0_32px_64px_-24px_rgba(23,22,15,0.22)]">
                  <Image
                    src={product.imageSrc}
                    alt={product.imageAlt}
                    fill
                    priority
                    quality={85}
                    sizes="(max-width: 1024px) 100vw, 700px"
                    className="object-cover transform-gpu transition-transform duration-[1200ms] ease-out group-hover:scale-[1.045]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/[0.04] via-transparent to-black/[0.12] pointer-events-none" />

                  {/* plate marker */}
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-4 bg-[#F7F5F0]/85 backdrop-blur-md border-t border-[#17160F]/[0.07]">
                    <span className="text-[10px] font-semibold tracking-[0.4em] text-[#736C5E] uppercase">
                      Plate
                    </span>
                    <span className="font-serif text-[15px] italic tracking-wide text-[#17160F]">
                      {product.numberStr}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Content, anchored by a vertical gold spine */}
              <div className="relative flex flex-col items-start w-full text-[#17160F] pl-8 sm:pl-10">
                <div className="absolute left-0 top-1 bottom-1 w-px bg-gradient-to-b from-[#9C7A46] via-[#9C7A46]/30 to-transparent" />

                <div className="text-[12px] sm:text-[13px] font-semibold tracking-[0.4em] text-[#9C7A46] uppercase">
                  {product.category} <span className="text-[#17160F]/25 mx-2">—</span> {product.year}
                </div>

                {/* Headline — was font-serif, now back to your font-display */}
                <h3 className="mt-5 sm:mt-6 flex flex-col text-[clamp(2.6rem,5.6vw,5.8rem)] font-display leading-[0.92] tracking-tight">
                  <span className="font-light text-[#17160F]">{product.titleBold}</span>
                  <span className="font-black uppercase tracking-tighter text-[#17160F]">
                    {product.titleOutline}
                  </span>
                </h3>

                <p className="mt-7 sm:mt-9 text-[15px] sm:text-[16px] leading-[1.75] text-[#5A5548] font-light max-w-[480px]">
                  {product.description}
                </p>

                <div className="mt-9 sm:mt-11 grid grid-cols-2 gap-x-10 gap-y-5 py-7 w-full border-t border-[#17160F]/[0.10] text-[11.5px] tracking-[0.15em] uppercase font-medium">
                  <div>
                    <p className="text-[#9C7A46] text-[10px] tracking-[0.3em] mb-1">Material</p>
                    <p className="text-[#17160F] font-normal normal-case text-[13px] tracking-normal">{product.specs.material}</p>
                  </div>
                  <div>
                    <p className="text-[#9C7A46] text-[10px] tracking-[0.3em] mb-1">Finish</p>
                    <p className="text-[#17160F] font-normal normal-case text-[13px] tracking-normal">{product.specs.finish}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-[#9C7A46] text-[10px] tracking-[0.3em] mb-1">Weight</p>
                    <p className="text-[#17160F] font-normal normal-case text-[13px] tracking-normal">{product.specs.weight}</p>
                  </div>
                </div>

                <Link href="/contact">
                  <motion.button
                    type="button"
                    whileHover="hover"
                    initial="rest"
                    className="group mt-4 sm:mt-6 relative w-full sm:w-auto overflow-hidden text-left rounded-none border border-[#17160F] px-10 py-4 text-[11px] font-semibold tracking-[0.3em] text-[#17160F] uppercase bg-transparent cursor-pointer"
                  >
                    <motion.span
                      variants={{ rest: { y: "100%" }, hover: { y: 0 } }}
                      transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
                      className="absolute inset-0 bg-[#17160F]"
                    />
                    <span className="relative inline-flex items-center gap-3 z-10 group-hover:text-[#F7F5F0] transition-colors duration-300">
                      Request Quotation
                      <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </span>
                  </motion.button>
                </Link>
              </div>

            </div>
          </section>
        ))}
      </div>

      {/* Fixed editorial index + progress rail */}
      <div className="pointer-events-none fixed bottom-8 left-0 right-0 z-20 px-6 sm:px-16 lg:px-24">
        <div className="max-w-[1360px] mx-auto flex items-center gap-6">
          <span className="font-display text-[13px] font-bold tracking-[0.1em] text-[#17160F] tabular-nums">
            {String(activeIndex + 1).padStart(2, "0")}
          </span>
          <div className="relative flex-1 h-px bg-[#17160F]/10">
            <div
              ref={progressRef}
              className="absolute top-0 left-0 h-px bg-[#9C7A46] transition-[width] duration-100"
              style={{ width: "0%" }}
            />
          </div>
          <span className="font-display text-[13px] font-bold tracking-[0.1em] text-[#17160F]/40 tabular-nums">
            {String(productsData.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </div>
  );
}